/**
 * Price formatting utilities for consistent price display across the application
 */

export interface FormatPriceOptions {
  /**
   * Disables the cents-detection heuristic used for raw backend values.
   * Enable this when you know the price is already normalized to dollars
   * (e.g., cart totals) to avoid accidental cent conversion on large integers.
   */
  disableCentsHeuristic?: boolean;
}

/**
 * Formats a price value to display as currency
 * @param price - The price value (can be number, string, or null)
 * @param currency - The currency symbol (default: '$')
 * @param decimals - Number of decimal places (default: 2)
 * @param options - Formatting options
 * @returns Formatted price string or 'N/A' if price is null/undefined
 */
export const formatPrice = (
  price: number | string | null | undefined,
  currency = "$",
  decimals = 2,
  options: FormatPriceOptions = {},
): string => {
  const { disableCentsHeuristic = false } = options;

  if (price === null || price === undefined) {
    return "N/A";
  }

  const priceIsString = typeof price === "string";
  const numericPrice = priceIsString ? parseFloat(price) : price;

  if (isNaN(numericPrice)) {
    return "N/A";
  }

  // Heuristic: treat as cents if it's a large integer (>= 10000).
  // For 3-digit integers (100-999), only treat as cents when the original input was a string
  // (common for raw API string values like "999" => $9.99). Numeric values in this range are
  // considered already normalized dollars.
  const isInCents =
    !disableCentsHeuristic &&
    Number.isInteger(numericPrice) &&
    (numericPrice >= 10000 ||
      (priceIsString && numericPrice >= 100 && numericPrice <= 999));

  const displayPrice = isInCents ? numericPrice / 100 : numericPrice;

  return `${currency}${displayPrice.toFixed(decimals)}`;
};

/**
 * Formats a price value to display as currency with locale formatting
 * @param price - The price value (can be number, string, or null)
 * @param currency - The currency symbol (default: '$')
 * @param locale - The locale for formatting (default: 'en-US')
 * @param options - Formatting options
 * @returns Formatted price string or 'N/A' if price is null/undefined
 */
export const formatPriceWithLocale = (
  price: number | string | null | undefined,
  currency = "$",
  locale = "en-US",
  options: FormatPriceOptions = {},
): string => {
  const { disableCentsHeuristic = false } = options;

  if (price === null || price === undefined) {
    return "N/A";
  }

  const priceIsString = typeof price === "string";
  const numericPrice = priceIsString ? parseFloat(price) : price;

  if (isNaN(numericPrice)) {
    return "N/A";
  }

  // Heuristic: treat as cents if it's a large integer (>= 10000),
  // or a 3-digit integer (100-999) which typically represents cents for <$10 prices.
  const isInCents =
    !disableCentsHeuristic &&
    Number.isInteger(numericPrice) &&
    (numericPrice >= 10000 ||
      (priceIsString && numericPrice >= 100 && numericPrice <= 999));

  const displayPrice = isInCents ? numericPrice / 100 : numericPrice;

  return `${currency}${displayPrice.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * Converts a price from cents to dollars
 * @param priceInCents - The price in cents
 * @returns The price in dollars
 */
export const convertCentsToDollars = (priceInCents: number): number => {
  return priceInCents / 100;
};

/**
 * Converts a price from dollars to cents
 * @param priceInDollars - The price in dollars
 * @returns The price in cents
 */
export const convertDollarsToCents = (priceInDollars: number): number => {
  return Math.round(priceInDollars * 100);
};

/**
 * Checks if a price value is in cents format
 * @param price - The price value to check
 * @returns True if the price appears to be in cents format
 */
export const isPriceInCents = (price: number): boolean => {
  return price >= 1000 && Number.isInteger(price);
};

/**
 * Normalizes a price value to dollars (converts from cents if needed)
 * @param price - The price value
 * @returns The normalized price in dollars
 */
export const normalizePrice = (price: number): number => {
  return isPriceInCents(price) ? convertCentsToDollars(price) : price;
};

// Unified backend price normalizer used across app
// kind: 'regular' uses conservative cents detection (>=10000 only)
// kind: 'list' treats 100–9999 as cents to handle list price variations
export type BackendPriceKind = "regular" | "list";

export const normalizeBackendPrice = (
  price: number | string | null | undefined,
  kind: BackendPriceKind = "regular",
): number | null => {
  if (price === null || price === undefined) return null;
  const priceIsString = typeof price === "string";
  const numericPrice = priceIsString ? parseFloat(price) : price;
  if (isNaN(numericPrice)) return null;

  if (!Number.isInteger(numericPrice)) return numericPrice;

  if (kind === "list") {
    // Convert 3-4 digit ints to dollars, keep larger ones in cents logic
    if (numericPrice >= 100 && numericPrice <= 9999) return numericPrice / 100;
    if (numericPrice >= 10000) return numericPrice / 100;
    return numericPrice;
  }

  // regular price:
  // - Only convert 3-digit integers (100-999) when the original value was a string (e.g., "999" => 9.99)
  // - Always convert large integers (>= 10000)
  if (
    (priceIsString && numericPrice >= 100 && numericPrice <= 999) ||
    numericPrice >= 10000
  ) {
    return numericPrice / 100;
  }
  return numericPrice;
};

/**
 * Normalizes a regular price with optional list price context for ambiguous 4-digit integers.
 * - If list price provided: choose the interpretation (dollars vs cents) closer to list price.
 * - If no list price: keep dollars for 4-digit values unless they are clearly implausible
 *   (>= $2000 as dollars and <= $500 as cents), in which case prefer cents.
 */
/**
 * Formats a TJS (Tajikistani Somoni) amount for display
 * @param amount - The amount in TJS
 * @returns Formatted string with 2 decimal places (e.g., "100,566.00")
 */
export const formatTjs = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const normalizeRegularPriceWithContext = (
  price: number | string | null | undefined,
  listPrice: number | string | null | undefined,
): number | null => {
  const normalizedList = normalizeBackendPrice(listPrice, "list");
  const normalizedBase = normalizeBackendPrice(price, "regular");
  if (normalizedBase === null) return null;

  const rawNumeric =
    typeof price === "string" ? parseFloat(price) : (price as number);
  if (!Number.isFinite(rawNumeric)) return normalizedBase;

  // Handle ambiguous 3- and 4-digit integers
  if (Number.isInteger(rawNumeric) && rawNumeric >= 100 && rawNumeric <= 9999) {
    const dollarsCandidate = normalizedBase; // from regular rule (likely same as raw)
    const centsCandidate = rawNumeric / 100;

    if (normalizedList !== null && Number.isFinite(normalizedList)) {
      const diffDollars = Math.abs(dollarsCandidate - normalizedList);
      const diffCents = Math.abs(centsCandidate - normalizedList);
      return diffCents <= diffDollars ? centsCandidate : dollarsCandidate;
    }

    // Fallback without list price:
    // - Prefer dollars by default to avoid converting valid $259 -> $2.59
    // - But if dollars look implausibly high and cents plausible, prefer cents
    //   4-digit: dollars >= 500 and cents <= 200 (e.g., 1918 -> 19.18)
    //   3-digit: dollars >= 500 and cents <= 50 (e.g., 999 -> 9.99)
    if (rawNumeric >= 1000) {
      if (dollarsCandidate >= 500 && centsCandidate <= 200)
        return centsCandidate;
    } else {
      if (dollarsCandidate >= 500 && centsCandidate <= 50)
        return centsCandidate;
    }
  }

  return normalizedBase;
};
